import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["initials", "avatarUrl"];
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'foundations-theming/utils';
import { Button } from 'foundations-components/transitional/components/Button';
import { View } from 'foundations-components/transitional/components/View';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Flex } from 'foundations-components/components/Flex';
import { Image } from 'foundations-components/transitional/components/Image';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Link } from 'foundations-components/transitional/components/Link';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { HR } from 'foundations-components/transitional/components/HR';
import { LogoutIcon } from 'foundations-assets/icons/components/LogoutIcon';
import { ShareIcon } from 'foundations-assets/icons/components/ShareIcon';
import { SettingsIcon } from 'foundations-assets/icons/components/SettingsIcon';
import { SettingsPanel } from './SettingsPanel';
import { useLoginVerifyQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc.hooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { getPortalInstanceId, getPortalAccountId } from '../utils/metadata';
import { useIsPillBorderRadius } from '../hooks/useIsPillBorderRadius';
import { ShareModal } from './ShareModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SHARE_PORTAL_GATE = 'PortalsPlatform::UnifiedShell::AccessDelegation';
const HoverContainer = styled.div.withConfig({
  displayName: "MyAccountPopover__HoverContainer"
})(({
  theme,
  $isPill
}) => ({
  borderRadius: $isPill ? theme.borderRadius['0'] : theme.borderRadius['100'],
  '&:hover': {
    backgroundColor: theme.color['fill-surface-default-hover']
  }
}));
function UserAvatar({
  initials,
  avatarUrl,
  size = 'medium'
}) {
  const dimensions = size === 'small' ? 32 : 40;
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);
  const showImage = Boolean(avatarUrl) && !imageFailed;
  if (showImage) {
    return /*#__PURE__*/_jsx(Image, {
      src: avatarUrl,
      alt: "",
      shape: "circle",
      width: dimensions,
      height: dimensions,
      bordered: true,
      borderColor: "border-core-default",
      onError: () => setImageFailed(true)
    });
  }
  return /*#__PURE__*/_jsx(Flex, {
    backgroundColor: "fill-accent-teal-default",
    borderRadius: "full",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-core-default",
    inlineSize: `${dimensions}px`,
    blockSize: `${dimensions}px`,
    alignItems: "center",
    justifyContent: "center",
    children: /*#__PURE__*/_jsx(Paragraph, {
      color: "text-inverse-default",
      marginBlock: "0",
      typography: size === 'small' ? 'detail-100' : 'body-100',
      children: initials
    })
  });
}
function AccountButton(_ref) {
  let {
      initials,
      avatarUrl
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(Button, Object.assign({}, props, {
    use: "unstyled",
    "aria-label": I18n.text('app.myAccountPopover.ariaLabel'),
    paddingInline: "0",
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "100",
      children: [/*#__PURE__*/_jsx(UserAvatar, {
        initials: initials,
        avatarUrl: avatarUrl,
        size: "small"
      }), /*#__PURE__*/_jsx(DownIcon, {
        size: "100",
        color: "text-core-default"
      })]
    })
  }));
}
export function MyAccountPopover() {
  var _loginVerifyData$feat, _loginVerifyData$feat2;
  const portalInstanceId = getPortalInstanceId();
  const accountId = getPortalAccountId();
  const isPill = useIsPillBorderRadius();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const {
    data: loginVerifyData,
    loading: loginVerifyLoading
  } = useLoginVerifyQuery({
    variables: {
      accountId,
      portalInstanceId
    }
  });
  const initials = useMemo(() => {
    if (!loginVerifyData) {
      return 'U';
    }
    const firstName = loginVerifyData.firstname || '';
    const lastName = loginVerifyData.lastname || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  }, [loginVerifyData]);
  const avatarUrl = loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.avatarUrl;
  const isShareEnabled = (_loginVerifyData$feat = loginVerifyData === null || loginVerifyData === void 0 || (_loginVerifyData$feat2 = loginVerifyData.featuresEnabled) === null || _loginVerifyData$feat2 === void 0 ? void 0 : _loginVerifyData$feat2.includes(SHARE_PORTAL_GATE)) !== null && _loginVerifyData$feat !== void 0 ? _loginVerifyData$feat : false;
  const renderButton = useCallback(props => /*#__PURE__*/_jsx(AccountButton, Object.assign({}, props, {
    initials: initials,
    avatarUrl: avatarUrl
  })), [initials, avatarUrl]);
  if (loginVerifyLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "extra-small"
    });
  }
  if (!loginVerifyData) {
    return /*#__PURE__*/_jsx(Button, {
      use: "primary",
      size: "small",
      href: `/_hcms/mem/login?redirect_url=${encodeURIComponent(window.location.href)}`,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.login"
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Dropdown, {
        buttonUse: "unstyled",
        menuWidth: 200,
        placement: "bottom left",
        autoPlacement: false,
        Button: renderButton,
        popoverProps: isPill ? {
          borderRadius: '0'
        } : undefined,
        open: isDropdownOpen,
        onOpenChange: evt => setIsDropdownOpen(evt.target.value),
        children: [/*#__PURE__*/_jsx(View, {
          paddingBlock: "50",
          paddingInline: "100",
          children: /*#__PURE__*/_jsx(HoverContainer, {
            $isPill: isPill,
            children: /*#__PURE__*/_jsx(Link, {
              use: "unstyled",
              onClick: () => setIsSettingsOpen(true),
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "200",
                padding: "100",
                paddingInlineStart: "200",
                children: [/*#__PURE__*/_jsx(IconHolder, {
                  icon: SettingsIcon,
                  compact: true,
                  color: "text-core-default",
                  size: "400"
                }), /*#__PURE__*/_jsx(Paragraph, {
                  color: "text-core-default",
                  typography: "body-100",
                  marginBlock: "0",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.myAccountPopover.settings.heading"
                  })
                })]
              })
            })
          })
        }), isShareEnabled && /*#__PURE__*/_jsx(View, {
          paddingBlock: "50",
          paddingInline: "100",
          children: /*#__PURE__*/_jsx(HoverContainer, {
            $isPill: isPill,
            children: /*#__PURE__*/_jsx(Link, {
              use: "unstyled",
              onClick: () => {
                setIsDropdownOpen(false);
                setIsShareModalOpen(true);
              },
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "200",
                padding: "100",
                paddingInlineStart: "200",
                children: [/*#__PURE__*/_jsx(IconHolder, {
                  icon: ShareIcon,
                  compact: true,
                  color: "text-core-default",
                  size: "400"
                }), /*#__PURE__*/_jsx(Paragraph, {
                  color: "text-core-default",
                  typography: "body-100",
                  marginBlock: "0",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.myAccountPopover.sharePortal"
                  })
                })]
              })
            })
          })
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsx(View, {
          paddingBlock: "100",
          paddingInline: "100",
          children: /*#__PURE__*/_jsx(HoverContainer, {
            $isPill: isPill,
            children: /*#__PURE__*/_jsx(Link, {
              use: "unstyled",
              href: "/_hcms/mem/logout",
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "200",
                padding: "100",
                paddingInlineStart: "200",
                children: [/*#__PURE__*/_jsx(IconHolder, {
                  icon: LogoutIcon,
                  compact: true,
                  color: "text-core-default",
                  size: "400"
                }), /*#__PURE__*/_jsx(Paragraph, {
                  color: "text-core-default",
                  typography: "body-100",
                  marginBlock: "0",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.myAccountPopover.logout"
                  })
                })]
              })
            })
          })
        })]
      }), isShareEnabled && portalInstanceId !== undefined && accountId !== undefined && /*#__PURE__*/_jsx(ShareModal, {
        open: isShareModalOpen,
        onClose: () => setIsShareModalOpen(false),
        portalInstanceId: portalInstanceId,
        accountId: accountId,
        portalUserRole: loginVerifyData.portalUserRole
      })]
    }), /*#__PURE__*/_jsx(SettingsPanel, {
      open: isSettingsOpen,
      onClose: () => setIsSettingsOpen(false),
      loginVerifyData: loginVerifyData
    })]
  });
}